/**
 * Chunked batch upload helper with progress tracking and retry.
 * Processes receipts in small batches (e.g. 5 at a time) to prevent mobile network timeouts.
 */

import { supabase } from "@/integrations/supabase/client";
import { compressImage } from "@/lib/imageCompression";
import { uploadWithRetry } from "@/lib/uploadRetry";

export interface BatchReceipt {
  id: string;
  file: File;
  uploadedFilePath?: string;
  merchant: string;
  total: number;
  date: string;
  category: string;
}

export interface ChunkProgress {
  processed: number;
  total: number;
  currentChunk: number;
  totalChunks: number;
  failed: number;
}

export interface ChunkResult {
  successCount: number;
  failedCount: number;
  receiptsToInsert: any[];
}

/**
 * Process receipts in chunks to prevent mobile network timeouts.
 * @param receipts - The batch receipts to process.
 * @param userId - The authenticated user ID.
 * @param chunkSize - How many receipts to process in parallel (default 5).
 * @param onProgress - Callback for progress updates.
 */
export async function processReceiptsInChunks(
  receipts: BatchReceipt[],
  userId: string,
  chunkSize: number = 5,
  onProgress?: (progress: ChunkProgress) => void
): Promise<ChunkResult> {
  const receiptsToInsert: any[] = [];
  let failedCount = 0;
  const totalChunks = Math.ceil(receipts.length / chunkSize);

  // Split into chunks
  const chunks: BatchReceipt[][] = [];
  for (let i = 0; i < receipts.length; i += chunkSize) {
    chunks.push(receipts.slice(i, i + chunkSize));
  }

  let processed = 0;

  for (let chunkIndex = 0; chunkIndex < chunks.length; chunkIndex++) {
    const chunk = chunks[chunkIndex];

    // Process all items in chunk in parallel
    const results = await Promise.allSettled(
      chunk.map(async (receipt) => {
        let fileName: string | undefined = receipt.uploadedFilePath;

        if (!fileName) {
          // Compress image before upload (aggressive for batch)
          const compressedFile = await compressImage(receipt.file, 1920, 1920, 0.7);
          const fileExt = receipt.file.name.split(".").pop() || "jpg";
          fileName = `${userId}/${crypto.randomUUID()}.${fileExt}`;

          const uploadResult = await uploadWithRetry(
            compressedFile,
            fileName,
            async (file, path) => supabase.storage.from("receipts").upload(path, file)
          );

          if (uploadResult?.error) {
            throw new Error(`Upload failed: ${uploadResult.error.message}`);
          }
        }

        return { receipt, fileName };
      })
    );

    // Collect successful uploads
    for (const result of results) {
      processed += 1;
      if (result.status === "fulfilled") {
        const { receipt, fileName } = result.value;
        receiptsToInsert.push({
          receipt,
          fileName,
        });
      } else {
        failedCount += 1;
        console.warn("Chunk upload failed:", result.reason);
      }
    }

    // Report progress after each chunk
    onProgress?.({
      processed,
      total: receipts.length,
      currentChunk: chunkIndex + 1,
      totalChunks,
      failed: failedCount,
    });
  }

  return {
    successCount: receiptsToInsert.length,
    failedCount,
    receiptsToInsert,
  };
}
